import type { EnticerSignal } from './EnticerSignals/types';
import type { JobCardProps } from './JobCard';
import type { JobCardDataWithBrandingEligibility } from './types';

import { ActionButtonsWrapper } from './ActionButtons/ActionButtonsWrapper';
import {
    ApplicationStatusBadge,
    hasApplicationStatusBadge,
} from './ApplicationStatusBadge/ApplicationStatusBadge';
import { AttributeComponent } from './Attribute/AttributeComponent';
import { AttributesContainer } from './Attribute/AttributesContainer';
import { SalaryAttributeComponent } from './Attribute/SalaryAttributeComponent';
import { useBrandingMigrationFlags } from './BrandingMigrationContext';
import { CompanyBannerImage } from './CompanyBanner/CompanyBannerImage';
import { CompanyInfo } from './CompanyInfo/CompanyInfo';
import { CompanyLogo } from './CompanyInfo/CompanyLogo';
import { EnticerSignalsContainer } from './EnticerSignals/EnticerSignalsContainer';
import { FlairTags } from './FlairTags/FlairTags';
import { JobCardHeading } from './JobCardHeader/JobCardHeading';
import { RelevanceSurvey } from './RelevanceSurvey/RelevanceSurvey';
import { SponsoredDisclaimer } from './SponsoredDisclaimer/SponsoredDisclaimer';
import { Box, Pressable } from '../../../mads';
import { useI18n } from '../../../shared/I18n';

import { colors } from '@indeed/ifl-tokens/react-native';
import { useState } from 'react';
import { Platform, StyleSheet } from 'react-native';

export function DefaultJobCard({
    pressed,
    saveButton,
    dislikeButton,
    jobCardProps,
    viewFullDetailsLabel,
    onPressJobCard,
    trackingKey,
    showApplicationStatusBadge = false,
}: {
    pressed: boolean;
    saveButton: React.ReactNode;
    dislikeButton: React.ReactNode;
    jobCardProps: JobCardProps;
    viewFullDetailsLabel: string;
    onPressJobCard?: () => void;
    trackingKey?: string;
    showApplicationStatusBadge?: boolean;
}) {
    const {
        data: job,
        clickEffectEnabled,
        actionButtonsStyle,
        loggedIn,
        appearanceMode,
        showRelevanceSurvey,
        position,
    } = jobCardProps;
    const i18n = useI18n();
    const { isBrandedJobMigrationTestActive } = useBrandingMigrationFlags();
    // Mirror the inner accessibility Pressable state so the full card shows pressed feedback.
    const [innerPressed, setInnerPressed] = useState(false);
    const href = jobCardProps?.webTargetUrl ?? `/viewjob?jobKey=${job.key}`;

    const isFeatured = appearanceMode === 'featured';
    const featuredData = job.featuredCardData;
    const jobWithBranding = job as JobCardDataWithBrandingEligibility;
    const subgraphBanner = isBrandedJobMigrationTestActive
        ? jobWithBranding.brandingEligibility?.shouldShowBranding
            ? jobWithBranding.brandingEligibility?.brandingInformation?.localizedAssets?.banner?.url
            : undefined
        : undefined;
    const bannerUrl = isFeatured
        ? (isBrandedJobMigrationTestActive ? subgraphBanner : featuredData?.headerImageUrl)
        : null;
    const hasBanner = typeof bannerUrl === 'string' && bannerUrl.length > 0;
    const hasEnticers = isFeatured && (featuredData?.enticerSignals?.length ?? 0) > 0;

    const hasApplicationBadge = hasApplicationStatusBadge(
        job.savedJob?.state,
        job.savedJob?.applicationDraft,
    );
    const showBadge = showApplicationStatusBadge && hasApplicationBadge;

    return (
        <Box
            pointerEvents="box-none"
            sx={styles.outerWrapper}
            {...Platform.select({ web: { href: href, target: '_self' } })}
        >
            <Box
                testID="job-card-container"
                sx={[
                    styles.container,
                    clickEffectEnabled && (pressed || innerPressed)
                        ? styles.containerPressed
                        : null,
                ]}
            >
                <Box sx={styles.cardBody}>
                    <Pressable
                        accessible={true}
                        accessibilityRole="button"
                        role="button"
                        testID="inner-view-details-pressable"
                        accessibilityLabel={viewFullDetailsLabel}
                        accessibilityHint={i18n.gettext('Tap to view job details')}
                        onPress={onPressJobCard}
                        onPressIn={() => setInnerPressed(true)}
                        onPressOut={() => setInnerPressed(false)}
                        sx={styles.cardContent}
                    >
                        <Box
                            accessibilityElementsHidden={true}
                            importantForAccessibility="no-hide-descendants"
                        >
                            {/* Company banner for featured cards */}
                            {hasBanner && (
                                <CompanyBannerImage
                                    url={bannerUrl}
                                    companyLogo={<CompanyLogo job={job} size="lg" />}
                                />
                            )}
                            {/* Header */}
                            <Box sx={styles.header}>
                                <Box sx={styles.headerLeft}>
                                    {/* Flair tags */}
                                    {!showBadge && <FlairTags job={job} />}

                                    {/* Application Status Badge */}
                                    {showApplicationStatusBadge && (
                                        <ApplicationStatusBadge
                                            savedJobState={job.savedJob?.state}
                                            applicationDraft={job.savedJob?.applicationDraft}
                                        />
                                    )}
                                    {/* Title */}
                                    <JobCardHeading
                                        job={job}
                                        pressed={pressed}
                                        clickEffectEnabled={clickEffectEnabled}
                                        appearanceMode={'default'}
                                    />
                                    {/* Company Info */}
                                    <Box sx={[styles.company]}>
                                        <CompanyLogo job={job} />
                                        <CompanyInfo job={job} appearanceMode={'default'} />
                                    </Box>
                                </Box>
                            </Box>
                            {/* Job attributes */}
                            <AttributesContainer appearanceMode={'default'}>
                                <SalaryAttributeComponent
                                    salary={job.compensation?.formattedText ?? ''}
                                    jobKey={job.key}
                                    isHighlightPositive={
                                        job.jobseekerPreferences?.paySentiment === 'POSITIVE'
                                    }
                                />
                                <AttributeComponent
                                    attributes={job.jobTypes}
                                    type="jobTypes"
                                    jobKey={job.key}
                                />
                                <AttributeComponent
                                    attributes={job.shiftAndSchedule}
                                    type="shiftAndSchedule"
                                    jobKey={job.key}
                                />
                                <AttributeComponent
                                    attributes={job.benefits}
                                    type="benefits"
                                    jobKey={job.key}
                                />
                            </AttributesContainer>
                            {/* EU sponsored disclaimer (renders nothing when not sponsored) */}
                            <SponsoredDisclaimer job={job} />
                        </Box>
                    </Pressable>
                    {/* Action Buttons */}
                    <ActionButtonsWrapper
                        sx={actionButtonsStyle}
                        loggedIn={loggedIn}
                        jobTitle={job.title}
                    >
                        <Box sx={styles.dislikeButtonWrapper}>{saveButton}</Box>
                        <Box>{dislikeButton}</Box>
                    </ActionButtonsWrapper>
                </Box>
                {showRelevanceSurvey && (
                    <RelevanceSurvey
                        jobKey={job.key}
                        trackingKey={trackingKey}
                        position={position}
                    />
                )}
                {/* TODO: Job Status, cf: https://indeed.sourcegraph.com/code.corp.indeed.com/mobile-app-platform/mobile-app-platform@1db2331a32c92d6c3248f2c573e750ee1e56003b/-/blob/apps/jsma-ios/Packages/CoworkerPlugin/Sources/CoworkerPlugin/Views/JobCardViews/JobCardSerp.swift?L81 */}
                {/* Enticer signals for featured cards */}
                {hasEnticers && (
                    <EnticerSignalsContainer
                        signals={featuredData!.enticerSignals as EnticerSignal[]}
                    />
                )}
            </Box>
        </Box>
    );
}

const styles = StyleSheet.create({
    outerWrapper: {
        flexGrow: 1,
    },
    container: {
        flexGrow: 1,
        padding: 16,
        backgroundColor: 'white',
        borderRadius: 20,
        borderWidth: 1,
        borderColor: colors.borderWeak,
        overflow: 'hidden',
    },
    containerPressed: {
        backgroundColor: colors.backgroundStrong,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
    },
    cardBody: {
        flexDirection: 'row',
        alignItems: 'flex-start',
    },
    cardContent: {
        flex: 1,
    },
    headerLeft: {
        flex: 1,
    },
    actionButtonsFeatured: {
        top: 100,
    },
    company: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 8,
        gap: 8,
    },
    dislikeButtonWrapper: {
        marginBottom: 16,
    },
});
